import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export const challengeKeys = {
  today: () => ['challenges', 'today'] as const,
  list: (params?: unknown) => ['challenges', 'list', params] as const,
  schedule: () => ['challenges', 'schedule'] as const,
};

export interface ChallengeOption {
  id: string;
  text: string;
  sortOrder: number;
}

/** What the learner is shown. Answers and teaching text arrive only once earned. */
export interface TodaysChallenge {
  id: string;
  question: string;
  topic: string;
  scheduledFor: string | null;
  /**
   * Null until they have answered.
   *
   * The correct option and the explanation are not sent to somebody who has
   * not answered yet — a question the whole company is about to be asked
   * should not have its answer sitting in the page source.
   */
  explanation: string | null;
  takeaway: string | null;
  hasHint: boolean;
  course: { id: string; title: string } | null;
  options: ChallengeOption[];
  myAnswer: {
    selectedOptionId: string;
    isCorrect: boolean;
    answeredAt: string;
    correctOptionId: string | null;
  } | null;
}

export interface ChallengeStreak {
  /** Consecutive published challenges answered — empty days are stepped over. */
  current: number;
  best: number;
  answeredToday: boolean;
}

export interface TodayResponse {
  /** Null on a day nobody scheduled anything. The card says so. */
  challenge: TodaysChallenge | null;
  streak: ChallengeStreak;
  /**
   * The ordinary learning streak, shown INSTEAD on a day with no challenge.
   *
   * On a day with nothing to answer, a challenge streak that cannot move is
   * noise — the one still moving is the one worth showing.
   */
  learningStreak: number;
  stats: {
    answered: number;
    correct: number;
    available: number;
    /** The same total the achievements page shows, not a challenge subtotal. */
    totalPoints: number;
  };
}

/** What a correct answer is worth. Mirrored from the API for the dialog. */
export const CHALLENGE_POINTS = 10;

export function useTodaysChallenge() {
  return useQuery({
    queryKey: challengeKeys.today(),
    queryFn: async () => {
      const { data } = await api.get<TodayResponse>('/challenges/today');
      return data;
    },
    /**
     * Held for the session rather than refetched on focus.
     *
     * It changes once a day. Refetching every time somebody tabs back would
     * be a request per tab-switch, for a payload that cannot have moved.
     */
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// ── Authoring ──────────────────────────────────────────────────────────────

export interface AuthorChallenge {
  id: string;
  question: string;
  topic: string;
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  scheduledFor: string | null;
  hint: string | null;
  explanation: string | null;
  takeaway: string | null;
  course: { id: string; title: string } | null;
  options: { id: string; text: string; isCorrect: boolean }[];
  answerCount: number;
  correctCount: number;
  /**
   * False once anyone has answered.
   *
   * The question, its options and which one is right freeze at that point —
   * changing them would rewrite who was correct and take back points people
   * earned honestly. The hint, explanation and takeaway stay editable.
   */
  canEditQuestion: boolean;
}

export interface ScheduleDay {
  day: string;
  isToday: boolean;
  challenge: {
    id: string;
    question: string;
    topic: string;
    status: string;
    answerCount: number;
  } | null;
}

export function useChallenges(params: Record<string, unknown>) {
  return useQuery({
    queryKey: challengeKeys.list(params),
    queryFn: async () => {
      const { data } = await api.get<{
        items: AuthorChallenge[];
        meta: { page: number; pageSize: number; total: number; totalPages: number };
      }>('/challenges', { params });
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useChallengeSchedule() {
  return useQuery({
    queryKey: challengeKeys.schedule(),
    queryFn: async () => {
      const { data } = await api.get<{ days: ScheduleDay[] }>('/challenges/schedule');
      return data.days;
    },
  });
}

/**
 * How a challenge performed, as a sentence rather than two numbers.
 *
 * Exported so the list and any future analytics agree — and because "0 of 0"
 * and "nobody has answered yet" are different things to read.
 */
export function describeResults(challenge: AuthorChallenge): string {
  if (challenge.answerCount === 0) {
    return challenge.scheduledFor ? 'No answers yet' : 'Not scheduled yet';
  }

  const percent = Math.round((challenge.correctCount / challenge.answerCount) * 100);
  return `${challenge.answerCount} answered · ${percent}% correct`;
}
